import { useEffect, useState } from "react";

import { API_GET_EMPLOYEE_FILE } from "../../../../config/Endpoints.js";
import { showError } from "../../../utils/toast.ts";

export type CertificationDocument = {
  url: string;
  contentType: string;
  name: string;
};

export function useCertificationDocument(accessToken?: string) {
  const [document, setDocument] = useState<CertificationDocument | null>(null);
  const [loadingPath, setLoadingPath] = useState("");

  const close = () => {
    setDocument((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const view = async (documentPath: string) => {
    if (!documentPath || loadingPath) return;
    setLoadingPath(documentPath);
    try {
      const response = await fetch(
        `${API_GET_EMPLOYEE_FILE}?filepath=${encodeURIComponent(documentPath)}`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        },
      );
      if (!response.ok) throw new Error("Unable to load certificate.");
      const blob = await response.blob();
      const contentType =
        response.headers.get("content-type")?.split(";")[0]?.trim() ||
        blob.type ||
        "application/octet-stream";
      const name =
        documentPath.split("/").filter(Boolean).pop() || "Certificate";
      close();
      setDocument({
        url: URL.createObjectURL(new Blob([blob], { type: contentType })),
        contentType,
        name,
      });
    } catch {
      showError("Unable to open certificate. Please try again.");
    } finally {
      setLoadingPath("");
    }
  };

  useEffect(() => () => {
    if (document) URL.revokeObjectURL(document.url);
  }, [document]);

  return { document, loadingPath, view, close };
}
